// pages/PricingListPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, IconButton, Tooltip, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Stack, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, Button
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SummarizeIcon from "@mui/icons-material/Summarize";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import { getPricings, deletePricing } from "../../services/pricingService";
import { getClientNameById } from "../../services/clientService";
import { useCompany } from "../../contexts/CompanyContext";

const PricingListPage = () => {
  const navigate = useNavigate();
  const { company, updateCompany } = useCompany(); 
  const companyId = company?.companyId || null; 
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!companyId) return;
      setLoading(true);
      setError("");
      try {
        const data = await getPricings(companyId);

        // Resolve nome do cliente quando só há idCliente
        const mapped = await Promise.all(
          data.map(async (p) => {
            let clientName = p.clientName || p.clienteNome || "";
            if (!clientName && p.idCliente) {
              clientName = await getClientNameById(companyId, p.idCliente);
            }
            return {
              id: p.id,
              projectName: p.nomeProjeto  || "—",
              clientName: clientName || "—",
              projectType: p.tipoProjeto || "—",
              ...p,
            };
          })
        );

        if (alive) setRows(mapped);
      } catch (e) {
        console.error(e);
        if (alive) setError("Não foi possível carregar as precificações.");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [companyId]);

  const handleEdit = (id) => navigate(`/pricing/${id}/edit`);
  const handleResumo = (id) => navigate(`/pricing/${id}/resume`);

  const askDelete = (id) => {
    setDeletingId(id);
    setConfirmOpen(true);
  };
  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeletingId(null);
  };
  const confirmDelete = async () => {
    if (!deletingId) return;
    try {
      await deletePricing(company, deletingId);
      setRows(prev => prev.filter(r => r.id !== deletingId));
      updateCompany({ ...company, pricingsCount: (company.pricingCount || 1) - 1 });
    } catch (e) {
      console.error(e);
      setError("Falha ao excluir. Tente novamente.");
    } finally {
      closeConfirm();
    }
  };

  const EmptyState = useMemo(() => (
    <Box py={8} display="flex" alignItems="center" justifyContent="center">
      <Stack spacing={2} alignItems="center">
        <Typography variant="h6">Nenhuma precificação encontrada</Typography>
        <Typography variant="body2" color="text.secondary">
          Crie sua primeira precificação
        </Typography>
      </Stack>
    </Box>
  ), []);



  return (
    <Box p={2} sx={{ width: "100%" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={700}>Precificações</Typography>
        <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ borderRadius: 3, mr: 2 }}
            onClick={() => navigate('/pricing/new')}
          >
            Nova Precificação
          </Button>
      </Stack>

      <Paper elevation={2} sx={{ overflow: "hidden" }}>
        {(() => {
          if (loading) {
            return (
              <Box p={6} display="flex" justifyContent="center">
                <CircularProgress />
              </Box>
            );
          }
          if (error) {
            return (
              <Box p={3}><Typography color="error">{error}</Typography></Box>
            );
          }
          if (rows.length === 0) {
            return EmptyState;
          }
          return (
            <TableContainer>
              <Table size="small" aria-label="lista de precificações">
                <TableHead>
                  <TableRow>
                    <TableCell>Projeto</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.nomeProjeto}</TableCell>
                      <TableCell>{row.clientName}</TableCell>
                      <TableCell>{row.tipoProjeto}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Tooltip title="Editar (abrir stepper)">
                            <IconButton size="small" onClick={() => handleEdit(row.id)}>
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Resumo (última etapa)">
                            <IconButton size="small" onClick={() => handleResumo(row.id)}>
                              <SummarizeIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => askDelete(row.id)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          );
        })()}
      </Paper>

      <Dialog open={confirmOpen} onClose={closeConfirm}>
        <DialogTitle>Excluir precificação?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Esta ação não pode ser desfeita. Tem certeza de que deseja excluir esta precificação?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancelar</Button>
          <Button onClick={confirmDelete} variant="contained" color="error">Excluir</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PricingListPage;
